import React, { useState } from "react";
import PropTypes from "prop-types";
import { Grid, Paper, Table, TableBody, TableCell, TableRow } from "@material-ui/core";

import AccionesRapidas from "../general/AccionesRapidas";
import TituloAccion from "../general/TituloAccion";
import BotonAtras from "./components/BotonAtras";
import ModalMasInformacion from "./components/ModalMasInformacion";
import ProcesoSolicitud from "./components/ProcesoSolicitud";
import { Cargando, EstadoVacio } from "./components/Estados";

import { useConsulta } from "./hooks/useConsulta";
import { obtenerSolicitud } from "./services/bibliotecaApi";
import { filasInformacionBasica } from "./model/presentacion";
import { puedeCancelar, puedeEditar } from "./model/permisos";

import revisar from "../../global/imagenes/revisar.png";
import correction from "../../global/imagenes/correction.png";
import cancelarIcono from "../../global/imagenes/cancel.png";

/** Tarjeta de accion, como las de Procesos Academicos. */
const Accion = ({ imagen, titulo, descripcion, onClick }) => (
  <Grid item xs={12}>
    <Paper elevation={1} style={{ border: "1px solid #e0e0e0" }} onClick={onClick}>
      <AccionesRapidas
        imagen={imagen}
        titulo={titulo}
        sub_titulo="Bien"
        des_sub_titulo={descripcion}
      />
    </Paper>
  </Grid>
);

Accion.propTypes = {
  imagen: PropTypes.string.isRequired,
  titulo: PropTypes.string.isRequired,
  descripcion: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * Detalle de una solicitud para quien la radico, como pantalla y no como
 * modal, igual que en Procesos Academicos: a la izquierda los datos, a la
 * derecha que se puede hacer, y abajo en que va el proceso. En Gestion el
 * detalle es otro: un modal con pestañas (gestion/ModalDetalleGestion).
 *
 * Lo que ocupa mucho (estudiantes, libros o niveles e historial) se abre
 * aparte, en "¡ Mas Informacion !".
 *
 * @param {number} version cambiarla vuelve a consultar, para ver los cambios
 *   recien guardados sin salir de la pantalla.
 * @param {?Function} onEditar se ofrece si es propia y sigue solicitada.
 * @param {?Function} onCancelar recibe la solicitud; con la misma regla.
 */
const DetalleSolicitud = ({
  id,
  usuario,
  version,
  volver,
  onEditar,
  onCancelar,
}) => {
  const { datos: solicitud, cargando, error } = useConsulta(
    () => obtenerSolicitud(id),
    [id, version],
    null,
  );
  const [masInformacion, setMasInformacion] = useState(false);

  if (cargando) return <Cargando />;

  if (error || !solicitud) {
    return (
      <div>
        <BotonAtras onClick={volver} />
        {/* El backend explica por que no se puede ver (ajena, borrada...). */}
        <EstadoVacio mensaje={error?.message ?? "No fue posible cargar la solicitud"} />
      </div>
    );
  }

  return (
    <div>
      <BotonAtras onClick={volver} />

      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} sm={12} md={6}>
          <TituloAccion principal="" alterno="¡ Información Básica !" />
          <Paper elevation={0} square className="scroll">
            <Table>
              <TableBody>
                {filasInformacionBasica(solicitud).map(([etiqueta, valor]) => (
                  <TableRow key={etiqueta}>
                    <TableCell>{etiqueta}</TableCell>
                    <TableCell>{valor}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <TituloAccion principal="" alterno="¿ Que deseas hacer ?" />
          <Paper elevation={0} square>
            <Grid container direction="row" spacing={1} style={{ width: "100%" }}>
              <Accion
                imagen={revisar}
                titulo="¡ Más Información !"
                descripcion=", aquí puedes ver los estudiantes, el material y el historial."
                onClick={() => setMasInformacion(true)}
              />

              {/* El mismo criterio del lapiz en la lista: solo las propias y
                  mientras siguen solicitadas. */}
              {onEditar && puedeEditar(solicitud, usuario) && (
                <Accion
                  imagen={correction}
                  titulo="¡ Editar Solicitud !"
                  descripcion=", aquí puedes corregir lo que enviaste."
                  onClick={onEditar}
                />
              )}

              {onCancelar && puedeCancelar(solicitud, usuario) && (
                <Accion
                  imagen={cancelarIcono}
                  titulo="¡ Cancelar Solicitud !"
                  descripcion=", aquí puedes cancelarla si ya no la necesitas."
                  onClick={() => onCancelar(solicitud)}
                />
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <TituloAccion principal="" alterno="¿ Como va mi proceso ?" />
          <ProcesoSolicitud solicitud={solicitud} />
        </Grid>
      </Grid>

      {masInformacion && (
        <ModalMasInformacion
          solicitud={solicitud}
          onCerrar={() => setMasInformacion(false)}
        />
      )}
    </div>
  );
};

DetalleSolicitud.propTypes = {
  id: PropTypes.number.isRequired,
  volver: PropTypes.func.isRequired,
  onEditar: PropTypes.func,
  onCancelar: PropTypes.func,
  usuario: PropTypes.object,
  version: PropTypes.number,
};

DetalleSolicitud.defaultProps = {
  version: 0,
  onEditar: null,
  onCancelar: null,
};

export default DetalleSolicitud;
